/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { ColumnFiltersState, SortingState, VisibilityState } from '@tanstack/react-table'
import { ReservationsTableRequest } from 'types/requests/ReservationsTableRequest'
import { Reservation } from 'types/entities/Reservation'
import { ReservationNote } from 'types/entities/ReservationNote'
import { PackagesCart } from 'types/entities/PackagesCart'
import { ReservationPlan } from 'types/entities/ReservationPlan'
import {
    SellerExtraInvoiceNoteToReservation,
    SellerExtraOfferNoteToReservation,
} from 'types/entities/SellerReservations'
import { WritableDraft } from 'immer'
import { DEFAULT_RESERVATIONS_TABLE_SORTING } from 'utils/reservations/reservationTableUtils'

export interface ReservationsTableUiState {
    pageIndex: number
    pageSize: number
    globalFilter: string
    columnFilters: ColumnFiltersState
    sorting: SortingState
    columnVisibility?: VisibilityState
}

interface ReservationsTableState {
    ui: ReservationsTableUiState
}

export interface ReservationsState {
    reservations: Reservation[]
    tableState: ReservationsTableRequest
    table: ReservationsTableState
}

export const DEFAULT_RESERVATIONS_TABLE_UI: ReservationsTableUiState = {
    pageIndex: 0,
    pageSize: 50,
    globalFilter: '',
    columnFilters: [],
    sorting: [...DEFAULT_RESERVATIONS_TABLE_SORTING],
}

const initialState: ReservationsState = {
    reservations: [],
    tableState: {
        filter: {},
        page: 0,
        per_page: 50,
        search: [],
        sorting: [...DEFAULT_RESERVATIONS_TABLE_SORTING],
    },
    table: {
        ui: { ...DEFAULT_RESERVATIONS_TABLE_UI },
    },
}

const reservationsSlice = createSlice({
    name: 'seller',
    initialState,
    reducers: {
        setReservations(state, action: PayloadAction<Reservation[]>) {
            state.reservations = action.payload
        },
        setReservation(state, action: PayloadAction<Reservation>) {
            const index = state.reservations.findIndex(
                (r) => r.id === action.payload.id
            )
            if (index === -1) state.reservations.push(action.payload)
            else state.reservations[index] = action.payload
        },
        setTableState(state, action: PayloadAction<ReservationsTableRequest>) {
            state.tableState = action.payload
        },
        setReservationsTableUiState(
            state,
            action: PayloadAction<Partial<ReservationsTableUiState>>
        ) {
            if (!state.table) {
                state.table = {
                    ui: { ...initialState.table.ui },
                }
            }
            if (!state.table.ui) {
                state.table.ui = { ...initialState.table.ui }
            }

            state.table.ui = {
                ...state.table.ui,
                ...action.payload,
            }
        },
        resetDefaultReservationsTable(state) {
            if (!state.table) {
                state.table = {
                    ui: { ...initialState.table.ui },
                }
                return
            }

            state.table.ui = { ...initialState.table.ui }
        },
        clearReservations(state) {
            state.reservations = []
        },
        clearTableState(state) {
            state.tableState = initialState.tableState
            state.table = {
                ui: { ...initialState.table.ui },
            }
        },
        deleteReservation(state, action: PayloadAction<Reservation>) {
            state.reservations = state.reservations.filter(
                (r) => r.id !== action.payload.id
            )
        },
        pushReservationNote(state, action: PayloadAction<ReservationNote>) {
            const reservation = state.reservations.find(
                (r) => r.id === action.payload.reservation_id
            )
            if (!reservation) return

            const index = reservation.reservationNotes.findIndex(
                (n) => n.id === action.payload.id
            )
            const draftNote = action.payload as WritableDraft<ReservationNote>

            if (index === -1) {
                reservation.reservationNotes.push(draftNote)
            } else {
                reservation.reservationNotes[index] = draftNote
            }
        },
        deleteReservationNote(
            state,
            action: PayloadAction<{ reservation_id: number; note_id: number }>
        ) {
            const reservation = state.reservations.find(
                (r) => r.id === action.payload.reservation_id
            )
            if (!reservation) return

            reservation.reservationNotes = reservation.reservationNotes.filter(
                (n) => n.id !== action.payload.note_id
            )
        },

        setPackageCartToReservation(
            state,
            action: PayloadAction<PackagesCart>
        ) {
            const reservation = state.reservations.find(
                (r) => r.id === action.payload.reservation_id
            )
            if (reservation) reservation.packagesCart = action.payload
        },
        setReservationPlans(
            state,
            action: PayloadAction<{
                reservation_id: number
                plans: ReservationPlan[]
            }>
        ) {
            const reservation = state.reservations.find(
                (r) => r.id === action.payload.reservation_id
            )
            if (reservation) {
                reservation.extra = {
                    ...reservation.extra,
                    reservation_plan: action.payload.plans,
                }
            }
        },
        setExtraInvoiceNoteToReservation(
            state,
            action: PayloadAction<SellerExtraInvoiceNoteToReservation>
        ) {
            const reservation = state.reservations.find(
                (r) => r.id === action.payload.reservation_id
            )
            if (reservation) {
                reservation.extra = {
                    ...reservation.extra,
                    invoice_note: action.payload.invoice_note,
                }
            }
        },
        setExtraOfferNoteToReservation(
            state,
            action: PayloadAction<SellerExtraOfferNoteToReservation>
        ) {
            const reservation = state.reservations.find(
                (r) => r.id === action.payload.reservation_id
            )
            if (reservation) {
                reservation.extra = {
                    ...reservation.extra,
                    offer_note: action.payload.offer_note,
                }
            }
        },
        resetState: () => initialState,
    },
})

export const {
    setReservations,
    setReservation,
    setTableState,
    setReservationsTableUiState,
    resetDefaultReservationsTable,
    clearReservations,
    clearTableState,
    deleteReservation,
    pushReservationNote,
    deleteReservationNote,
    setPackageCartToReservation,
    setReservationPlans,
    setExtraInvoiceNoteToReservation,
    setExtraOfferNoteToReservation,
    resetState,
} = reservationsSlice.actions

export default reservationsSlice.reducer
